import { getIsAndroidSdk, getIsIosSdk, getIsWebSdk } from '@app/store';
import { Events, GrantStatus } from '@constants';
import { httpRequest } from '@lib';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { BadgeTypes } from '@leegality/leegality-react-component-library';
import { notification } from '@components/notification';
import { getSdkHostWindow } from './parent-messenger';

export const isFunction = (functionToCheck) => {
  return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
};

export const downloadFile = (fileData, fileName) => {
  if (fileData && fileName) {
    const url = window.URL.createObjectURL(fileData);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
};

// export const downloadFileFromUrl = (url) => {
//   if (getIsAndroidSdk() && isFunction(window.ConsentManagerSDK.downloadFile)) {
//     window.ConsentManagerSDK.downloadFile(url, 'consentRecordlogs.xml', 'xml');
//   } else if (getIsIosSdk()) {
//     window.location = `consentmanagerdownload://${encodeURIComponent(`${url}:consentRecordlogs.xml:xml`)}`;
//   } else {
//     const link = document.createElement('a');
//     link.href = url;
//     link.setAttribute('download', 'consentRecordlogs.xml');
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   }
// };

// export const downloadPdf = async (url, fileName = 'default-name.pdf') => {
//   const { data } = await httpRequest.get(url, {
//     responseType: 'blob',
//     headers: {
//       'Content-Type': 'application/pdf',
//     },
//     ignoreExtraHeaders: true,
//   });
//   const blob = new Blob([data], { type: 'application/pdf' });

//   if (getIsAndroidSdk() && isFunction(window.ConsentManagerSDK.downloadFile)) {
//     window.ConsentManagerSDK.downloadFile(url, fileName, 'pdf');
//   } else if (getIsIosSdk()) {
//     window.location = `consentmanagerdownload://${encodeURIComponent(`${url}:${fileName}:pdf`)}`;
//   } else {
//     downloadFile(blob, fileName);
//   }
// };

export function splitOnFirst(string, delimiter) {
  const index = string.indexOf(delimiter);
  if (index === -1) return [string];
  return [string.slice(0, index), string.slice(index + delimiter.length)];
}

export function formatDate(dateString) {
  if (!dateString) return '';

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = months[date.getMonth()];
  const day = date.getDate();
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  hours = hours || 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const formattedDate = `${month} ${day < 10 ? `0${day}` : day}, ${year}`;
  const formattedTime = `${hours}:${formattedMinutes} ${ampm}`;

  return `${formattedDate} ${formattedTime}`;
}

export function formatDueDate(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function convertObject(dictionary) {
  return Object.keys(dictionary).reduce((result, key) => ({ ...result, [key]: key }), {});
}

export function isPageInIframe() {
  return (window.location !== window.parent.location);
}

export const getDefaultFieldFormId = (defaultField, formFields) => {
  return formFields && Object.keys(formFields)
    .filter(
      (key) => formFields[key].type === defaultField,
    )[0];
};

export const getDefaultFieldValue = ({
  defaultField, requestFormData, selectedRequestTypeId, data,
}) => {
  return requestFormData[getDefaultFieldFormId(defaultField, data?.requestForms[selectedRequestTypeId]?.formFields)].value;
};

export const getStatusBadgeProps = (status, getTranslation) => {
  const statusMap = {
    [GrantStatus.GRANTED]: { type: BadgeTypes.SUCCESS, label: getTranslation('GRANTED_LABEL') },
    [GrantStatus.REVOKED]: { type: BadgeTypes.ERROR, label: getTranslation('REVOKED_LABEL') },
    [GrantStatus.INACTIVE]: { type: BadgeTypes.GRAY, label: getTranslation('INACTIVE_LABEL') },
  };
  return statusMap[status] || statusMap.INACTIVE;
};

export function getBadgeTypeBasisConsentGrantStatus(status) {
  switch (status) {
    case GrantStatus.GRANTED:
      return BadgeTypes.SUCCESS;
    case GrantStatus.REVOKED:
      return BadgeTypes.ERROR;
    default:
      return BadgeTypes.WARNING;
  }
}

export const convertEnumToReadableString = (enumString) => {
  if (!enumString) return enumString;
  return enumString
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

async function urlToBase64(fileUrl) {
  const response = await fetch(fileUrl);
  const blob = await response.blob();

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.readAsDataURL(blob);
  });
}

export const downloadFileGeneric = async (url, fileName) => {
  try {
    if (!url && getIsIosSdk()) {
      // eslint-disable-next-line no-param-reassign
      url = 'https://run.mocky.io/v3/c63fa11d-92fa-4451-8403-f879046c7f25';
    }
    if (!url || !fileName) {
      notification.error({ text: 'Url or fileName is missing' });
      console.log('Url or fileName is missing');
      return;
    }
    const fileExtension = fileName.split('.').pop().toLowerCase();
    const mimeType = {
      pdf: 'application/pdf',
      xml: 'application/xml',
      txt: 'text/plain',
      json: 'application/json',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
    }[fileExtension] || 'application/octet-stream';

    const { data } = await httpRequest.get(url, {
      responseType: 'blob',
      headers: {
        'Content-Type': mimeType,
      },
      ignoreExtraHeaders: true,
      ignoreAuthToken: true,
    });

    const blob = new Blob([data], { type: mimeType });

    if (getIsAndroidSdk() && isFunction(window.ConsentManagerSDK.downloadFile)) {
      window.ConsentManagerSDK.downloadFile(url, fileName, fileExtension);
    } else if (getIsIosSdk()) {
      const base64 = await urlToBase64(url);
      window.location = `consentmanagerdownload://${encodeURIComponent(`${fileName}:${base64}`)}`;
    } else {
      downloadFile(blob, fileName);
    }
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

export const communicateResponseToSDK = (response) => {
  let resp = response;
  if (response?.error) { // APIs with error non-400 has response.error instead of response.data.errors
    resp = { code: 'GENERIC_ERROR', errors: [{ message: 'Something went wrong', code: 'SOMETHING_WENT_WRONG' }] };
  }

  if (!response) {
    resp = { code: 'CANCELLED', errors: [{ message: 'User cancelled the operation', code: 'USER_CANCELLED' }] };
  }

  if (getIsAndroidSdk() && isFunction(window.ConsentManagerSDK.response)) {
    window.ConsentManagerSDK.response(JSON.stringify(resp));
  }
  if (getIsIosSdk()) {
    window.location = `triggerevent://${encodeURIComponent(`data:${JSON.stringify(resp)}`)}`;
  }

  // Parent page in iframe mode, the opener in popout mode.
  const sdkHost = getSdkHostWindow();
  if (getIsWebSdk() && sdkHost) {
    sdkHost.postMessage({
      eventType: Events.COMMUNICATE_RESPONSE,
      data: resp,
    }, '*');
  }
};

// export const getUniqueDataCategories = (activities) => {
//   const categoriesMap = new Map();

//   activities.forEach((activity) => {
//     activity?.dataCategories?.forEach((category) => {
//       if (category?.id && !categoriesMap.has(category.id)) {
//         categoriesMap.set(category.id, category);
//       }
//     });
//   });

//   return [...categoriesMap.values()];
// };

export function interpolateEndpoint(str, obj) {
  let a = str;
  Object.keys(obj)
    .forEach((key) => {
      a = a.replace(`{${key}}`, obj[key]);
    });

  return a;
}

export function getUniqueDataCategories(processingActivities) {
  const all = processingActivities.flatMap((activity) => (activity.systems || []).flatMap((system) => system.dataCategories || []));
  return Array.from(
    all.reduce((map, dc) => {
      if (dc && !map.has(dc.id)) map.set(dc.id, dc);
      return map;
    }, new Map()).values(),
  );
}

// Sends the *page the user sees* to `url`. Inside an iframe, `window.location`
// is the frame's own — assigning it would only navigate the frame — so an
// embedded app navigates the top-level window. Browsers allow that from a
// cross-origin frame right after a user action (the consent click); if the
// embedder blocks top navigation (sandbox without allow-top-navigation) it
// throws, and the frame falls back to an `_top` window.open.
export const navigateTopWindow = (url) => {
  const isEmbedded = window.parent !== window;
  try {
    (isEmbedded ? window.top : window).location.assign(url);
  } catch {
    window.open(url, '_top');
  }
};

export const isValidRedirectUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    return ['http:', 'https:'].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

// Helper function to request geolocation from browser
export const requestGeolocation = () => new Promise((resolve) => {
  if (!navigator.geolocation) {
    resolve({ geoLatitude: null, geoLongitude: null });
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      resolve({
        geoLatitude: position.coords.latitude,
        geoLongitude: position.coords.longitude,
      });
    },
    (error) => {
      console.log('Geolocation permission denied or error:', error.message, error.code);
      resolve({ geoLatitude: null, geoLongitude: null });
    },
  );
});
